import { z } from 'zod';
import type { WebsiteEditRegion } from '../lib/website-templates/edit-regions';
import { isRecord, stableSlotJson } from '../lib/website-templates/internal';
import { validateSlotValue, type WebsiteSlotBatch } from '../lib/website-templates/slots';
import type {
  StoreWebsiteDocument,
  WebsiteIssueCode,
  WebsiteSlotSchema,
  WebsiteSlotValue,
  WebsiteTemplateManifest,
} from '../types/website-templates';

/**
 * C3 region form (A21). One dialog edits the slots of one region and saves
 * them as ONE batch. Admin only: the issue messages are Admin locale keys,
 * so this module is exported from `@dukanti/shared/admin`, never from the
 * public entry.
 *
 * The server validates every value again. This schema only spares the
 * merchant a save that cannot pass.
 */
export type WebsiteRegionFieldState =
  | { readonly mode: 'authored'; readonly value: WebsiteSlotValue }
  /** Nothing of the merchant is stored: the template default shows, if there is one. → `null` in the batch. */
  | { readonly mode: 'default' }
  /** "Leave empty". → `{ empty: true }` in the batch. Optional slots only. */
  | { readonly mode: 'empty' };

/** Keyed by slot id. */
export type WebsiteRegionFormValues = Readonly<Record<string, WebsiteRegionFieldState>>;

const ERRORS = 'admin.websiteTemplates.errors';
const MEDIA_ERRORS = 'admin.websiteTemplates.media.error';

/** Issue code → Admin locale key of the message shown under the field. */
export function websiteRegionIssueMessageKey(code: WebsiteIssueCode): string {
  switch (code) {
    case 'unsafe_text':
      return `${ERRORS}.unsafeText`;
    case 'text_too_long':
      return `${ERRORS}.tooLong`;
    case 'poster_required':
      return `${MEDIA_ERRORS}.posterRequired`;
    case 'media_kind_not_allowed':
      return `${MEDIA_ERRORS}.type`;
    case 'template_unavailable':
      return `${ERRORS}.templateUnavailable`;
    case 'builder_retired':
      return `${ERRORS}.builderRetired`;
    case 'rate_limited':
      return `${ERRORS}.rateLimited`;
    case 'slot_retired':
      return `${ERRORS}.slotRetired`;
    case 'invalid_slot':
    case 'unknown_slot':
    case 'structure_override':
    case 'invalid_media':
    case 'cross_store_reference':
    case 'invalid_reference':
    case 'contrast_insufficient':
    case 'unsupported_font':
    case 'unsupported_color_token':
    case 'missing_required':
    case 'missing_merchant_value':
    case 'missing_store_logo':
    case 'stale_draft':
    case 'document_too_large':
    case 'theme_locked':
      return `${ERRORS}.invalidValue`;
  }
}

/** What is wrong with one field, or null. The same rule the schema applies. */
export function websiteRegionFieldIssue(
  schema: WebsiteSlotSchema,
  state: unknown,
): WebsiteIssueCode | null {
  if (!isRecord(state)) return 'invalid_slot';
  const keys = Object.keys(state);
  if (state.mode === 'default') return keys.length === 1 ? null : 'invalid_slot';
  if (state.mode === 'empty') {
    if (keys.length !== 1) return 'invalid_slot';
    return schema.required ? 'missing_required' : null;
  }
  if (state.mode !== 'authored' || keys.length !== 2 || !keys.includes('value')) {
    return 'invalid_slot';
  }
  // "Leave empty" has its own mode; an authored value always carries `v`.
  if (isRecord(state.value) && Object.prototype.hasOwnProperty.call(state.value, 'empty')) {
    return 'invalid_slot';
  }
  const result = validateSlotValue(schema, state.value);
  return result.ok ? null : result.code;
}

/**
 * Zod schema of one region's form. Every slot of the region must be present
 * and no other key may be. An issue sits on the path `[slotId]` and its
 * message is an Admin locale key.
 */
export function buildWebsiteRegionFormSchema(
  manifest: WebsiteTemplateManifest,
  region: WebsiteEditRegion,
): z.ZodType<WebsiteRegionFormValues> {
  const schemaById = new Map(manifest.slots.map((slot) => [slot.id, slot]));
  const issue = (context: z.RefinementCtx, slotId: string, code: WebsiteIssueCode): void => {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: [slotId],
      message: websiteRegionIssueMessageKey(code),
      params: { code },
    });
  };

  return z
    .custom<WebsiteRegionFormValues>(isRecord, {
      message: websiteRegionIssueMessageKey('invalid_slot'),
    })
    .superRefine((values, context) => {
      for (const slotId of Object.keys(values)) {
        if (!region.slotIds.includes(slotId)) issue(context, slotId, 'unknown_slot');
      }
      for (const slotId of region.slotIds) {
        const schema = schemaById.get(slotId);
        if (!schema) {
          issue(context, slotId, 'unknown_slot');
          continue;
        }
        const code = websiteRegionFieldIssue(schema, values[slotId]);
        if (code) issue(context, slotId, code);
      }
    });
}

/** What the dialog shows when it opens: the stored draft, slot by slot. */
export function initialWebsiteRegionFormValues(
  manifest: WebsiteTemplateManifest,
  document: StoreWebsiteDocument,
  region: WebsiteEditRegion,
): WebsiteRegionFormValues {
  const known = new Set(manifest.slots.map((slot) => slot.id));
  const values: Record<string, WebsiteRegionFieldState> = {};
  for (const slotId of region.slotIds) {
    if (!known.has(slotId)) continue;
    const stored = Object.prototype.hasOwnProperty.call(document.slots, slotId)
      ? document.slots[slotId]
      : undefined;
    if (stored === undefined) values[slotId] = { mode: 'default' };
    else if ('empty' in stored) values[slotId] = { mode: 'empty' };
    else values[slotId] = { mode: 'authored', value: stored };
  }
  return values;
}

/**
 * The batch of one Save: ONLY the slots whose state differs from what the
 * dialog opened with. An untouched value is never sent, so an untouched rich
 * text (bold, italic, an ordered list of a frozen legacy page) is never
 * rewritten.
 *
 * Returns `{}` when nothing changed. The dialog keeps Save disabled while it
 * is `{}`, and `saveSlots` refuses `{}` before any request.
 */
export function toWebsiteSlotBatch(
  initial: WebsiteRegionFormValues,
  current: WebsiteRegionFormValues,
): WebsiteSlotBatch {
  const batch: Record<string, WebsiteSlotValue | null> = {};
  for (const slotId of Object.keys(current)) {
    const state = current[slotId];
    if (state === undefined) continue;
    const before = Object.prototype.hasOwnProperty.call(initial, slotId)
      ? initial[slotId]
      : undefined;
    if (before !== undefined && stableSlotJson(before) === stableSlotJson(state)) continue;
    if (state.mode === 'authored') batch[slotId] = state.value;
    else if (state.mode === 'empty') batch[slotId] = { empty: true };
    else batch[slotId] = null;
  }
  return batch;
}
